// ---------- une partie contre l'IA : les couloirs, le ruban, le dialogue, le clavier du jeu ----------
//
// Depuis le 2026-11-04 (shared/ai.ts), chaque ligne se joue contre l'IA :
//   - classique : l'IA donne un indice, le joueur propose un mot de la bonne longueur, l'IA relance ;
//   - inversé : le joueur voit le mot et le fait deviner à l'IA, un mot-indice par coup.
// Les couloirs et le ruban du mot mystère restent ceux de la maquette « I » : une ligne trouvée
// pose ses lettres, et sa lettre du ruban épelle peu à peu le mot mystère.
//
// LE SERVEUR TIENT LA PARTIE. Chaque coup part au Worker (src/ai.ts), qui rend l'état entier : on
// ne fait que le redessiner. Rien ne se joue hors ligne.
//
// `render` construit une fois, à l'ouverture ; `paint` bascule les classes et réécrit les textes
// (le dialogue, la saisie) — jamais les couloirs : les reconstruire couperait le tremblement d'une
// proposition fausse et les confettis en plein vol (CLAUDE.md, « Le DOM »).
//
// Le clavier est CELUI DU JEU : il ne pousse pas la page et ne se ferme pas, et la saisie passe par
// `type()`, depuis lui ou un clavier physique.

import { gridWord } from '../shared/normalize';
import { BONUS, type AiAction, type AiReply, type AiRow } from '../shared/ai';
import type { PuzzlePayload } from '../shared/types';
import { aiAct, aiAd, aiState, AiOffline } from './ai';
import { coach, dismissCoach } from './coach';
import { watchRewarded } from './ads';
import { settings } from './prefs';
import { burst } from './fx';
import { t, type I18nKey } from './i18n';
import { buzz } from './platform';
import { $, esc, ICON, toast, topBar } from './ui';

export interface PlayHooks {
  close(): void;
  finished(p: PuzzlePayload, stars: number): void;
}

/** Les deux claviers du jeu (Réglages → Clavier). */
const LAYOUTS = {
  azerty: ['AZERTYUIOP', 'QSDFGHJKLM', 'WXCVBN'],
  abc: ['ABCDEFGHIJ', 'KLMNOPQRST', 'UVWXYZ'],
};
/** Une saisie : un mot, pas une phrase (le Worker coupe aussi, à 40). */
const MAX_ENTRY = 20;

let pz: PuzzlePayload;
let hooks: PlayHooks;
let reply: AiReply | null = null;
let cur = 0;
/** ce que la saisie vise : la ligne en cours, ou le mot mystère (classique seulement) */
let aim: 'row' | 'mystery' = 'row';
let entry = '';
let busy = false;
let failed = false;
let wired = false;
/** Le numéro de l'ouverture : une réponse du réseau arrivée APRÈS qu'on a quitté la partie, ou
 *  ouvert une autre, est jetée (le même schéma que `ticket` dans src/main.ts). */
let opening = 0;
let finishTimer = 0;

const inverse = (): boolean => pz.mode === 'inverse';
const row = (i: number): AiRow | undefined => reply?.state.rows[i];
const wordOf = (i: number): string => pz.lines[i].enigma.word;
const locked = (r: AiRow): boolean => !r.done && r.moves >= r.allow;

export function closePuzzle(): void {
  clearTimeout(finishTimer);
  dismissCoach();
  opening++;
  hooks.close();
}

export function openPuzzle(p: PuzzlePayload, line: string, h: PlayHooks): void {
  clearTimeout(finishTimer);
  pz = p;
  hooks = h;
  reply = null;
  cur = 0;
  aim = 'row';
  entry = '';
  busy = true;
  failed = false;
  render(line);
  if (!wired) {
    wired = true;
    $('play').addEventListener('click', onClick);
    document.addEventListener('keydown', onKey);
  }
  paint();
  void load(++opening);
}

async function load(mine: number): Promise<void> {
  busy = true;
  failed = false;
  paint();
  try {
    let r = await aiState(pz.id);
    // Une partie déjà finie se rouvre pour être REJOUÉE (src/main.ts) : on la recommence ici,
    // côté serveur — ses étoiles restent acquises dans la progression du joueur.
    if (mine === opening && r.state.stars !== undefined)
      r = await aiAct(pz.id, { kind: 'restart' });
    if (mine !== opening) return;
    reply = r;
    const first = r.state.rows.findIndex((x) => !x.done);
    cur = Math.max(0, first);
  } catch {
    if (mine !== opening) return;
    failed = true;
  }
  busy = false;
  paint();
  // Les bulles d'aide APRÈS l'état : elles montrent le dialogue et le compteur, vides avant.
  if (reply && mine === opening) coach($('play'), pz.mode);
}

function render(line: string): void {
  const cols = pz.width;
  const ROWS = LAYOUTS[settings().keyboard];
  $('play').innerHTML = `
    ${topBar(line, `<span class="wallet" aria-live="polite"></span><button class="icon-btn" type="button" data-act="close" aria-label="${t('bar.close')}">${ICON.close}</button>`)}
    <div class="lanes-box">
      <div class="lanes" style="--cols:${cols};--col:${pz.col}">
        <div class="spine" aria-hidden="true"></div>
        ${pz.lines
          .map((l, i) => {
            const w = wordOf(i);
            let cells = '';
            for (let c = 0; c < cols; c++) {
              const j = c - l.offset;
              cells +=
                j >= 0 && j < w.length
                  ? `<span class="cell${c === pz.col ? ' col' : ''}" data-i="${i}" data-j="${j}"></span>`
                  : '<span class="gap"></span>';
            }
            return `<div class="lane" data-lane="${i}">
              <button type="button" class="lane-bg" data-act="lane" data-i="${i}" aria-label="${esc(t('ai.laneAria', { n: i + 1 }))}"></button>
              <div class="clues"></div>
              <div class="cells">${cells}</div>
            </div>`;
          })
          .join('')}
      </div>
    </div>
    <div class="dialog" aria-live="polite"></div>
    <div class="entry-bar">
      <button class="btn btn-soft btn-mys" type="button" data-act="aim">${t('ai.mystery')}</button>
      <div class="entry"></div>
    </div>
    <div class="kbd" role="group" aria-label="${t('play.keyboard')}">
      ${ROWS.slice(0, 2)
        .map(
          (r) =>
            `<div class="krow">${[...r].map((ch) => `<button type="button" class="key" data-key="${ch}">${ch}</button>`).join('')}</div>`,
        )
        .join('')}
      <div class="krow">
        <button type="button" class="key key-fn" data-act="erase" aria-label="${t('play.erase')}">${ICON.erase}</button>
        ${[...ROWS[2]].map((ch) => `<button type="button" class="key" data-key="${ch}">${ch}</button>`).join('')}
        <button type="button" class="key key-fn key-ok" data-act="send"><b>${t('ai.send')}</b></button>
      </div>
    </div>`;
}

/** La longueur attendue de la saisie — affichée, jamais imposée —, ou 0 pour un indice du mode
 *  inversé, qui n'en a pas. */
function expected(): number {
  if (aim === 'mystery') return pz.word.length;
  return inverse() ? 0 : wordOf(cur).length;
}

function laneClues(i: number): string {
  const r = row(i);
  if (!r) return '';
  const words = r.clues.map((c) => `<span>${esc(c)}</span>`).join('<i>·</i>');
  return `${words}<b class="moves">${t('ai.moves', { n: r.moves, max: r.allow })}</b>`;
}

/** Le fil de la ligne en cours, du plus ancien au plus récent : qui a dit quoi. */
function thread(i: number): string {
  const r = row(i)!;
  const out: string[] = [];
  const bot = `<b class="who">${t('ai.bot')}</b>`;
  const you = `<b class="who">${t('ai.you')}</b>`;
  if (inverse()) {
    out.push(
      `<p class="say sys">${t('ai.inverseHelp', { word: pz.lines[i].enigma.answer.toUpperCase() })}</p>`,
    );
    r.clues.forEach((c, k) => {
      out.push(`<p class="say me">${you}${esc(c)}</p>`);
      const g = r.guesses[k];
      if (g)
        out.push(
          `<p class="say bot ${gridWord(g) === wordOf(i) ? 'ok' : 'no'}">${bot}${esc(g)}</p>`,
        );
    });
  } else {
    out.push(`<p class="say sys">${t('ai.classicHelp', { n: wordOf(i).length })}</p>`);
    const n = Math.max(r.clues.length, r.guesses.length);
    for (let k = 0; k < n; k++) {
      if (r.clues[k]) out.push(`<p class="say bot">${bot}${esc(r.clues[k])}</p>`);
      const g = r.guesses[k];
      if (g)
        out.push(
          `<p class="say me ${gridWord(g) === wordOf(i) ? 'ok' : 'no'}">${you}${esc(g)}</p>`,
        );
    }
  }
  if (r.done === 2)
    out.push(
      `<p class="say sys">${t('ai.revealed', { word: pz.lines[i].enigma.answer.toUpperCase() })}</p>`,
    );
  if (busy) out.push(`<p class="say sys thinking">${t('ai.thinking')}</p>`);
  if (locked(r)) {
    const can = (reply?.bonus ?? 0) >= 1;
    out.push(`<div class="stuck"><p>${t('ai.locked')}</p>
      ${can ? `<button class="btn btn-go" type="button" data-act="more">${t('ai.more', { m: BONUS.moves })}</button>` : `<button class="btn btn-hint" type="button" data-act="ad">${ICON.play}${t('ai.ad', { n: BONUS.ad })}</button>`}
    </div>`);
  }
  if (!r.done)
    out.push(
      `<button class="link-btn" type="button" data-act="reveal">${t(inverse() ? 'ai.skip' : 'ai.reveal', { n: BONUS.reveal })}</button>`,
    );
  return out.join('');
}

function paint(): void {
  const root = $('play');
  const s = reply?.state;
  root.querySelector('.wallet')!.textContent = reply ? t('ai.bonus', { n: reply.bonus }) : '';
  root.querySelector('.spine')!.className =
    `spine${s?.mystery ? ' ok' : aim === 'mystery' ? ' on' : ''}`;
  root.querySelectorAll<HTMLElement>('.lane').forEach((lane) => {
    const i = Number(lane.dataset.lane);
    const r = row(i);
    lane.classList.toggle('on', aim === 'row' && i === cur);
    lane.classList.toggle('solved', !!r?.done);
    lane.querySelector('.clues')!.innerHTML = laneClues(i);
  });
  root.querySelectorAll<HTMLElement>('.cell').forEach((cell) => {
    const i = Number(cell.dataset.i);
    const j = Number(cell.dataset.j);
    const r = row(i);
    const pick = j === pz.lines[i].pick;
    // En inversé, le joueur CONNAÎT les mots : ils sont écrits d'avance, en pâle tant que l'IA ne
    // les a pas trouvés. En classique, une case se remplit quand sa ligne est trouvée, ou — pour
    // la case du ruban — quand le mot mystère l'est.
    const shown = inverse() || !!r?.done || (pick && !!s?.mystery);
    cell.textContent = shown ? wordOf(i)[j] : '';
    cell.classList.toggle('ok', r?.done === 1);
    cell.classList.toggle('given', r?.done === 2);
    cell.classList.toggle('ghost', inverse() && !r?.done);
    cell.classList.toggle('mys-ok', !!s?.mystery && pick);
    cell.classList.toggle('in', aim === 'mystery' ? pick : i === cur);
  });
  // le dialogue
  const dialog = root.querySelector<HTMLElement>('.dialog')!;
  if (failed)
    dialog.innerHTML = `<div class="stuck"><p>${t('ai.offline')}</p><button class="btn btn-go" type="button" data-act="retry">${t('ai.retry')}</button></div>`;
  else if (!reply) dialog.innerHTML = `<p class="say sys thinking">${t('ai.thinking')}</p>`;
  else if (aim === 'mystery')
    dialog.innerHTML = `<p class="say sys">${t('ai.mysteryOn', { n: pz.word.length })}</p>`;
  else dialog.innerHTML = thread(cur);
  dialog.scrollTop = dialog.scrollHeight;
  // La saisie : une ligne LIBRE, et la longueur attendue en compteur à côté. Pas de cases : un mot
  // trop long ou trop court se propose quand même et compte un coup (choix du 2026-11-04) — le
  // compteur aide sans interdire.
  const n = expected();
  const hint =
    aim === 'mystery'
      ? t('ai.mysteryPlaceholder', { n })
      : n
        ? t('ai.guessPlaceholder', { n })
        : t('ai.cluePlaceholder');
  root.querySelector('.entry')!.innerHTML =
    `<span class="free">${esc(entry) || `<em>${hint}</em>`}</span>` +
    (n ? `<b class="count${entry.length === n ? ' fit' : ''}">${entry.length}/${n}</b>` : '');
  const mys = root.querySelector<HTMLButtonElement>('.btn-mys')!;
  mys.hidden = inverse() || !!s?.mystery;
  mys.classList.toggle('on', aim === 'mystery');
  const r = row(cur);
  const closed = !reply || busy || (aim === 'row' && (!r || !!r.done || locked(r)));
  root.querySelector<HTMLButtonElement>('.key-ok')!.disabled = closed || !entry;
  root
    .querySelector(`.lane[data-lane="${cur}"]`)
    ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
}

function type(ch: string): void {
  if (entry.length >= MAX_ENTRY) return;
  entry += ch;
  paint();
}

function erase(): void {
  entry = entry.slice(0, -1);
  paint();
}

const REFUSED: Record<NonNullable<AiReply['refused']>, I18nKey> = {
  family: 'ai.refused.family',
  repeat: 'ai.refused.repeat',
  locked: 'ai.refused.locked',
  empty: 'ai.refused.empty',
  funds: 'ai.refused.funds',
};

/** Une action au serveur, puis ce qu'elle a changé à l'écran : confettis d'une ligne trouvée,
 *  tremblement d'un coup raté, toasts des bonus gagnés et de la tentative de l'IA. */
async function act(a: AiAction): Promise<void> {
  if (busy || !reply) return;
  const mine = opening;
  const before = reply;
  busy = true;
  paint();
  let next: AiReply;
  try {
    next = await aiAct(pz.id, a);
  } catch (e) {
    if (mine !== opening) return;
    busy = false;
    toast(t(e instanceof AiOffline ? 'ai.offline' : 'ai.error'));
    return paint();
  }
  if (mine !== opening) return;
  busy = false;
  reply = next;
  if (next.refused) {
    toast(t(REFUSED[next.refused], { n: expected() }));
    return paint();
  }
  entry = '';
  const i = 'row' in a ? a.row : cur;
  const was = before.state.rows[i];
  const now = next.state.rows[i];
  const lane = $('play').querySelector<HTMLElement>(`.lane[data-lane="${i}"]`)!;
  if (!was.done && now.done === 1) {
    const r = lane.getBoundingClientRect();
    burst(r.left + r.width / 2, r.top + r.height / 2, 30, 0.8);
    void buzz();
    toast(
      t(inverse() ? 'ai.aiFound' : 'play.found', { word: pz.lines[i].enigma.answer.toUpperCase() }),
    );
  } else if (a.kind === 'play' && now.moves > was.moves && !now.done) {
    lane.classList.remove('wrong');
    void lane.offsetWidth;
    lane.classList.add('wrong');
  }
  if (next.mysteryTry && !next.state.mystery)
    toast(t('ai.mysteryTry', { word: next.mysteryTry.toUpperCase() }));
  if (!before.state.mystery && next.state.mystery) {
    toast(t('play.mysteryFound'));
    const spine = $('play').querySelector('.spine')!.getBoundingClientRect();
    burst(spine.left + spine.width / 2, spine.top + 40, 50, 1);
    aim = 'row';
  } else if (a.kind === 'mystery' && !next.state.mystery) {
    toast(t('play.wrong'));
  }
  if (next.earned) setTimeout(() => toast(t('ai.earned', { n: next.earned! })), 900);
  // La ligne en cours est finie : on passe à la suivante qui ne l'est pas.
  if (now.done && cur === i) {
    const k = next.state.rows.findIndex((x) => !x.done);
    if (k >= 0) cur = k;
  }
  paint();
  if (next.state.stars !== undefined) {
    const done = pz;
    const h = hooks;
    const stars = next.state.stars;
    dismissCoach();
    finishTimer = window.setTimeout(() => h.finished(done, stars), 900);
  }
}

function send(): void {
  if (!entry) return;
  if (aim === 'mystery') return void act({ kind: 'mystery', row: cur, text: entry });
  void act({ kind: 'play', row: cur, text: entry });
}

async function ad(): Promise<void> {
  const mine = opening;
  if (!(await watchRewarded()) || mine !== opening) return;
  try {
    const bonus = await aiAd();
    if (mine !== opening || !reply) return;
    reply = { ...reply, bonus };
    toast(t('ai.earned', { n: BONUS.ad }));
  } catch {
    toast(t('ai.offline'));
  }
  paint();
}

function onClick(e: Event): void {
  const el = (e.target as HTMLElement).closest<HTMLElement>('[data-act], [data-key]');
  if (!el) return;
  if (el.dataset.key) return type(el.dataset.key);
  switch (el.dataset.act) {
    case 'close':
      return closePuzzle();
    case 'lane':
      cur = Number(el.dataset.i);
      aim = 'row';
      entry = '';
      return paint();
    case 'aim':
      aim = aim === 'mystery' ? 'row' : 'mystery';
      entry = '';
      return paint();
    case 'erase':
      return erase();
    case 'send':
      return send();
    case 'more':
      return void act({ kind: 'bonus', row: cur });
    case 'reveal':
      return void act({ kind: 'reveal', row: cur });
    case 'ad':
      return void ad();
    case 'retry':
      return void load(++opening);
  }
}

/** Un clavier physique (web, tablette) : les lettres, l'effacement, Entrée, et les flèches. */
function onKey(e: KeyboardEvent): void {
  if ($('play').hidden || e.ctrlKey || e.metaKey || e.altKey) return;
  if (e.key === 'Backspace') {
    e.preventDefault();
    return erase();
  }
  if (e.key === 'Enter') {
    e.preventDefault();
    if (!$('play').querySelector<HTMLButtonElement>('.key-ok')!.disabled) send();
    return;
  }
  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
    e.preventDefault();
    cur = (cur + (e.key === 'ArrowDown' ? 1 : -1) + pz.lines.length) % pz.lines.length;
    aim = 'row';
    entry = '';
    return paint();
  }
  const ch = gridWord(e.key);
  if (ch.length === 1) {
    e.preventDefault();
    type(ch);
  }
}
